/**
 * KAIZEN SmartPDV — generic device/employee authorization model.
 * Server is authoritative; UI permission switches are never a security boundary.
 * Device records here are administrative placeholders, NOT hardware pairing.
 */
export const CASHIER_CAPABILITIES=Object.freeze([
 'pdv_presale','pdv_sell','pdv_register','pdv_customers','pdv_catalog',
 'pdv_inventory','pdv_cash_management','pdv_reports','pdv_audit',
 'pdv_void_sales','pdv_discount_override','pdv_negative_stock_checkout'
]);
export const DEVICE_CAPABILITIES=Object.freeze([
 'pdv_presale','pdv_sell','pdv_register','pdv_customers','pdv_catalog',
 'pdv_inventory','pdv_cash_management','pdv_reports','pdv_audit',
 'pdv_void_sales','pdv_discount_override','pdv_negative_stock_checkout'
]);
const allCaps=new Set(CASHIER_CAPABILITIES);
const UUID=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KEY=/^[A-Za-z0-9_-]{4,90}$/;
const EMAIL=/^[^\s@]{1,64}@[^\s@]{1,253}$/;
const fail=code=>{const e=new Error(code);e.code=code;throw e};
export const validUuid=value=>typeof value==='string'&&UUID.test(value);
export function checkedCapabilities(values,managerCapabilities=CASHIER_CAPABILITIES){
 if(!Array.isArray(values)||values.length>allCaps.size||
   values.some(s=>!allCaps.has(s))||new Set(values).size!==values.length)
  fail('invalid_capability_list');
 const canGrant=new Set(managerCapabilities);
 if(values.some(s=>!canGrant.has(s)))fail('manager_cannot_grant_capability');
 return Object.freeze([...values]);
}
export function draftTerminal({siteId,name,deviceType,organizationGrants,managerOrgIds=[],managerCapabilities=CASHIER_CAPABILITIES}){
 if(!validUuid(siteId)||typeof name!=='string'||name.trim().length<3||name.trim().length>60)
  fail('invalid_device_identity');
 if(!['desktop','laptop','tablet','phone','other'].includes(deviceType))fail('invalid_device_type');
 if(!Array.isArray(organizationGrants)||organizationGrants.length<1||organizationGrants.length>25)
  fail('invalid_device_grants');
 const allowedOrg=new Set(managerOrgIds),seen=new Set();
 const grants=organizationGrants.map(g=>{
  if(!validUuid(g.organization_id)||!allowedOrg.has(g.organization_id)||seen.has(g.organization_id))
   fail('device_org_outside_manager_scope');
  seen.add(g.organization_id);
  return {organization_id:g.organization_id,capabilities:checkedCapabilities(g.capabilities,managerCapabilities)};
 });
 return {
  site_id:siteId,name:name.trim(),device_type:deviceType,
  grants,status:'pending_pairing',paired:false,can_process_real_sales:false,
  pairing_secret:null,owner_device_activation_required:true
 };
}
export function draftOperatorInvite({email,organizationId,capabilities,terminalIds=[],managerOrgIds=[],managerCapabilities=CASHIER_CAPABILITIES}){
 const norm=typeof email==='string'?email.trim().toLowerCase():'';
 if(norm.length>254||!EMAIL.test(norm)||!validUuid(organizationId)||!managerOrgIds.includes(organizationId))
  fail('invalid_invitation_scope');
 if(!Array.isArray(terminalIds)||terminalIds.length>50||terminalIds.some(v=>!validUuid(v))||
    new Set(terminalIds).size!==terminalIds.length)fail('invalid_terminal_assignments');
 return {
  email:norm,organization_id:organizationId,
  requested_capabilities:checkedCapabilities(capabilities,managerCapabilities),
  terminal_ids:[...terminalIds],role:'operator',status:'pending_owner_review',
  auth_user_created:false,login_sent:false,unconditional_access:false
 };
}
/** Enforce live permission intersection at every protected request, never by client filter. */
export function effectiveDeviceCapabilities({user,terminal,organizationId,sessionValid,entitlementActive,
 fiscalCleared=false}={}){
 if(!validUuid(organizationId)||sessionValid!==true||entitlementActive!==true||
    !user||user.active!==true||!user.orgs?.[organizationId]?.active||
    !terminal||terminal.status!=='active'||terminal.paired!==true||
    !terminal.grants?.[organizationId]?.active)
   return Object.freeze([]);
 const restrictions=user.orgs[organizationId].terminal_ids;
 if(Array.isArray(restrictions)&&restrictions.length>0&&!restrictions.includes(terminal.id))
   return Object.freeze([]);
 const userCaps=new Set(user.orgs[organizationId].capabilities||[]);
 const deviceCaps=new Set(terminal.grants[organizationId].capabilities||[]);
 const actual=DEVICE_CAPABILITIES.filter(c=>userCaps.has(c)&&deviceCaps.has(c));
 // A device and an operator cannot bypass legal/go-live approval.
 return Object.freeze(fiscalCleared?actual:actual.filter(c=>c!=='pdv_sell'));
}
export function canManageTeam({membership,entitlementActive,siteAllowed=false}={}){
 return Boolean(entitlementActive===true&&siteAllowed===true&&membership?.status==='active'&&
  (['owner','admin'].includes(membership.role)||membership.permissions?.pdv_terminal_manage===true));
}
